import React from 'react';

import camelCaseToTitleCase from 'common/util/camelCaseToTitleCase';
import getNullEmptyValue from 'common/util/getNullEmptyValue';
import { formatPrice } from 'common/formatters';

import {
  UTILITY_ACTIVATION_STATUS_LIST,
  UTILITY_ACTIVATION_STATUS_CANCEL_REASON_LIST,
} from 'common/utility/constants';

import { transformBooleanToYesNo } from 'client/helpers/formikHelpers';

import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import {
  SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
  SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE,
  SR_TABLE_COLUMN_FILTER_TYPE_TEXT,
} from 'client/components/SrTableColumnFilter';

const enumOptions = (list) => list.map((value) => ({ value, label: camelCaseToTitleCase(value) }));

// TODO(@mathew-ship-it): switch partnerName / providerName to categorical multi-select
// once a partner-list/provider-list endpoint is exposed by the UP API. Tracked separately
// from FEAT-B40 because it requires a backend addition that does not block this rollout.

export function makeAccountingDashboardRows({ rows, highlightRowId, setHighlightRowId }) {
  return rows.map((row) => ({
    onClick: () => {
      setHighlightRowId(row.id);
    },
    className: highlightRowId === row.id ? 'highlighted' : '',
    cells: [
      {
        dataTableFieldId: 'renterName',
        label: 'Renter Name',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search name…' },
      },
      {
        dataTableFieldId: 'renterNumber',
        label: 'Renter Number',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search number…' },
      },
      {
        dataTableFieldId: 'partnerName',
        label: 'Partner',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search partner…' },
      },
      {
        dataTableFieldId: 'addressStreetDisplay',
        label: 'Address',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search address…' },
      },
      {
        dataTableFieldId: 'addressStateCode',
        label: 'State',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'e.g. TX' },
      },
      {
        dataTableFieldId: 'providerName',
        label: 'Provider',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Search provider…' },
      },
      {
        // UPT Link is a derived UI element with no underlying data column. Per the locked
        // "every column gets controls" decision, expose a text filter targeting the
        // underlying activation short ID so a UPT link in hand can find its row.
        dataTableFieldId: 'utilityActivationShortId',
        label: 'UPT Link',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_TEXT, placeholder: 'Activation ID' },
        content: (
          <SrLink
            to={
              `/utility-profit/admin/processing/${row.utilityActivationShortId}` +
              `?backUrl=${encodeURIComponent('/utility-profit/admin/dashboard/accounting')}` +
              `&backTitle=${encodeURIComponent('Back to Accounting Dashboard')}`
            }
            target="_blank"
          >
            <SrText
              font="greycliff"
              color="blue300"
              cursor="pointer"
              weight="800"
              fontSize="16px"
              lineHeight="50px"
            >
              Link
            </SrText>
          </SrLink>
        ),
      },
      {
        dataTableFieldId: 'invoiceAmount',
        label: 'Actual Revenue to UP',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE },
        value: typeof row.invoiceAmount === 'number' ? formatPrice(row.invoiceAmount) : '--',
      },
      {
        dataTableFieldId: 'payoutAmount',
        label: 'Payout to Partner',
        filterConfig: { type: SR_TABLE_COLUMN_FILTER_TYPE_NUMBER_RANGE },
        value: typeof row.payoutAmount === 'number' ? formatPrice(row.payoutAmount) : '--',
      },
      {
        dataTableFieldId: 'activationStatus',
        label: 'Activation Status',
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
          options: enumOptions(UTILITY_ACTIVATION_STATUS_LIST),
        },
        value: camelCaseToTitleCase(row.activationStatus),
      },
      {
        dataTableFieldId: 'activationCancelReason',
        label: 'Activation Cancel Reason',
        filterConfig: {
          type: SR_TABLE_COLUMN_FILTER_TYPE_CATEGORICAL,
          options: enumOptions(UTILITY_ACTIVATION_STATUS_CANCEL_REASON_LIST),
        },
        value: camelCaseToTitleCase(row.activationCancelReason),
      },
    ].map((cell) => {
      const rawValue = 'value' in cell ? cell.value : row[cell.dataTableFieldId];
      const content = cell.content
        ? cell.content
        : typeof rawValue === 'boolean'
        ? transformBooleanToYesNo(rawValue, true)
        : getNullEmptyValue(rawValue, '--');

      return {
        ...cell,
        content,
      };
    }),
  }));
}

/**
 * Builds the header config for the Non-Internet accounting dashboard table.
 *
 * Headers are derived from the cell config so column order, ids, and labels stay in lockstep
 * with the row data. No props are required because all categorical filter options here come
 * from static enum lists.
 */
export function getAccountingDashboardHeaders() {
  const syntheticCells = makeAccountingDashboardRows({ rows: [{}] })[0].cells;
  return syntheticCells.map(({ dataTableFieldId, label, filterConfig }) => ({
    dataTableFieldId,
    content: label,
    filterConfig,
    sortPosition: 'right',
  }));
}

// Backwards-compat shim — kept for any existing callers; new code should call
// `getAccountingDashboardHeaders()` even though no args are required, for consistency
// with the Internet dashboard.
export const accountingDashboardHeaders = getAccountingDashboardHeaders();
